import { useState } from "react";
import { useForm, type UseFormRegister } from "react-hook-form";
import type {
  CatalogEntry,
  CatalogEntryInput,
  CatalogEntryUpdate,
  CatalogName,
} from "@core/types";
import {
  Checkbox,
  Field,
  FieldError,
  FileUploadChip,
  FormLayout,
  ImagePreviewBox,
  inputStyle,
  fieldStyle,
  PortalButton,
  useApiFieldErrors,
  useConfirmUnsavedChanges,
} from "@portal/common";
import { tokens } from "@core/theme/tokens";
import {
  CATALOG_ENTRY_FORM_FIELDS,
  catalogEntrySchema,
  type CatalogEntryFormValues,
} from "./catalogSchemas";
import { MERCHANT_TYPES, presentationFor } from "./catalogPresentation";
import { useEntries } from "./catalogQueries";

function isBilingual(entry: CatalogEntry | undefined, bilingual: boolean) {
  return bilingual || (entry ? typeof entry.name !== "string" : false);
}
function text(
  value: CatalogEntry["name"] | CatalogEntry["description"],
  language: "es" | "en",
) {
  return typeof value === "string" || value === null
    ? (value ?? "")
    : (value[language] ?? "");
}
const untranslatedPlaceholder = "Sin traducir · se mostrará en español";
// Campo "Ciudad" compartido por taller y comercio (admin.jsx:2870, 3704):
// ambos catálogos solo guardan cityId y ofrecen el mismo select — extraído
// para no duplicar el bloque (jscpd, quality-gate 2026-08-24).
function CityField({
  cities,
  register,
  error,
}: {
  cities: CatalogEntry[] | undefined;
  register: UseFormRegister<CatalogEntryFormValues>;
  error?: string | undefined;
}) {
  return (
    <label style={fieldStyle}>
      <span
        style={{
          font: `600 12px ${tokens.font.ui}`,
          color: tokens.text.secondary,
        }}
      >
        Ciudad
      </span>
      <select {...register("cityId")} style={inputStyle}>
        <option value="">Selecciona una ciudad</option>
        {cities?.map((city) => (
          <option key={city.id} value={city.id}>
            {text(city.name, "es")}
          </option>
        ))}
      </select>
      {error !== undefined ? <FieldError message={error} /> : null}
    </label>
  );
}
export function CatalogEntryForm({
  catalog,
  bilingual,
  entry,
  onSubmit,
  onCancel,
  submitting,
  modal,
  submitError,
  onUnhandledSubmitError,
}: {
  catalog: CatalogName;
  bilingual: boolean;
  entry: CatalogEntry | undefined;
  onSubmit: (input: CatalogEntryInput | CatalogEntryUpdate) => void;
  onCancel: () => void;
  submitting: boolean;
  // Revisión en vivo 2026-09-22, punto #4A: este formulario ES el diálogo,
  // igual que `MemberForm`/`UserForm`/`ProfileForm`. Con `modal`, el título y
  // los botones dejan de desplazarse con el contenido y pasan a las regiones
  // fijas del `Modal`. Lo pasa quien abre el diálogo (`CatalogsScreen`), que
  // es quien conoce el ancho por catálogo. Opcional como en `MemberForm`: sin
  // `modal` el formulario se renderiza inline, que es como lo montan sus
  // pruebas unitarias.
  modal?: { label: string; width?: number | string; onClose?: () => void };
  // Caso real (trace f4575316-5c75-4c27-83d0-340db0aafd9f): el 400 de la API
  // llega al `onError` de la mutación, que vive en `CatalogsScreen` (dueño
  // del `useToast`), no acá — así que el error entra por prop. Cuando la API
  // identificó el campo (`problem.errors[].field`), este formulario lo
  // asocia a su input con `applyApiFieldErrors`; si no pudo (sin `errors`, o
  // un `field` que este formulario no tiene), se lo devuelve al padre por
  // `onUnhandledSubmitError` para que muestre el toast de siempre
  // (`reportCatalogError`) — nunca se pierde el error, y nunca se duplica
  // mostrándolo en los dos lados a la vez.
  submitError?: unknown;
  onUnhandledSubmitError?: (error: unknown) => void;
}) {
  const [language, setLanguage] = useState<"es" | "en">("es");
  const existingAttributes = entry?.attributes as
    Record<string, unknown> | undefined;
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
    setError,
  } = useForm<CatalogEntryFormValues>({
    defaultValues: {
      code: entry?.code ?? "",
      nameEs: text(entry?.name ?? "", "es"),
      nameEn: text(entry?.name ?? "", "en"),
      descriptionEs: text(entry?.description ?? null, "es"),
      descriptionEn: text(entry?.description ?? null, "en"),
      active: entry?.active ?? true,
      sortOrder: entry?.sortOrder ?? undefined,
      color: String(existingAttributes?.color ?? ""),
      isFinal: Boolean(existingAttributes?.isFinal),
      version: String(existingAttributes?.version ?? ""),
      photoUrl: String(existingAttributes?.photoUrl ?? ""),
      hex: String(existingAttributes?.hex ?? ""),
      referenceKm: existingAttributes?.referenceKm as number | undefined,
      durationMin: existingAttributes?.durationMin as number | undefined,
      cityId: String(existingAttributes?.cityId ?? ""),
      address: String(existingAttributes?.address ?? ""),
      phone: String(existingAttributes?.phone ?? ""),
      bays: existingAttributes?.bays as number | undefined,
      noticeEmails: Array.isArray(existingAttributes?.noticeEmails)
        ? (existingAttributes.noticeEmails as string[]).join(", ")
        : "",
      type: String(existingAttributes?.type ?? ""),
      contact: String(existingAttributes?.contact ?? ""),
      logoUrl: String(existingAttributes?.logoUrl ?? ""),
      photoFileId: "",
      logoFileId: "",
    },
  });
  const cities = useEntries("cities", false, "");
  const photoUrl = watch("photoUrl");
  const logoUrl = watch("logoUrl");
  const isFinal = watch("isFinal");
  const active = watch("active");
  // Revisión en vivo 2026-09-22, punto #21: como en "Nueva recompensa", el
  // título del diálogo es el nombre que se está escribiendo (o el actual).
  const nameEs = watch("nameEs");
  useConfirmUnsavedChanges(isDirty);
  // El campo que manda la API es un `string` suelto (`Problem.errors[].field`);
  // `setError` de react-hook-form solo tipa las claves conocidas de
  // `CatalogEntryFormValues`, de ahí el cast. `CATALOG_ENTRY_FORM_FIELDS`
  // filtra un campo que la API identifica pero que este formulario no
  // renderiza (`attributes`, un atributo desconocido): ese caso cae al
  // toast (`onUnhandledSubmitError`) en vez de perderse en un `setError`
  // sin input que lo muestre.
  useApiFieldErrors(
    submitError,
    setError as (
      field: string,
      error: { type: string; message: string },
    ) => void,
    CATALOG_ENTRY_FORM_FIELDS,
    onUnhandledSubmitError,
  );
  const presentation = presentationFor(catalog);
  const bilingualEntry = isBilingual(entry, bilingual);
  const submit = (values: CatalogEntryFormValues) => {
    const parsed = catalogEntrySchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues)
        setError(issue.path[0] as keyof CatalogEntryFormValues, {
          message: issue.message,
        });
      return;
    }
    if (presentation.hasAttributes === "workshop" && !values.cityId) {
      setError("cityId", {
        message: "Ciudad es obligatoria para un taller.",
      });
      return;
    }
    if (
      presentation.hasAttributes === "merchant" &&
      (!values.cityId || !values.type)
    ) {
      // PLAN-009 §3.6: un mensaje que cubría dos campos se asignaba a uno
      // solo (`cityId`) aunque el que faltara fuera Tipo — cada campo con
      // su propio mensaje.
      if (!values.cityId) {
        setError("cityId", {
          message: "Ciudad es obligatoria para un comercio.",
        });
      }
      if (!values.type) {
        setError("type", {
          message: "Tipo de aliado es obligatorio para un comercio.",
        });
      }
      return;
    }
    const data = parsed.data;
    // El contrato distingue "en" ausente (traducción todavía pendiente,
    // translationComplete: false) de "en": "" (cadena vacía, que el backend
    // rechaza — catalog.service.ts:isBilingual solo acepta undefined o un
    // string no vacío). Antes de este fix se mandaba siempre la cadena vacía
    // cuando el operador no tocaba la pestaña EN, así que crear o editar
    // cualquier entrada de un catálogo bilingüe (member-actions,
    // referral-actions, reward/benefit/offer-categories, maintenance-types,
    // service-types) fallaba con 400 "Invalid catalog name" — reproducido con
    // agent-browser (run 2026-08-24 pasada 3) creando en Tipos de
    // mantenimiento sin llenar "Nombre (EN)".
    const name = bilingualEntry
      ? { es: values.nameEs, ...(values.nameEn ? { en: values.nameEn } : {}) }
      : values.nameEs;
    const description = bilingualEntry
      ? values.descriptionEs || values.descriptionEn
        ? {
            es: values.descriptionEs,
            ...(values.descriptionEn ? { en: values.descriptionEn } : {}),
          }
        : null
      : values.descriptionEs || null;
    const attributes: Record<string, unknown> = {};
    if (
      presentation.hasAttributes === "action" &&
      catalog === "referral-actions"
    )
      Object.assign(attributes, {
        color: values.color || undefined,
        isFinal: values.isFinal,
      });
    else if (presentation.hasAttributes === "action")
      Object.assign(attributes, {
        color: values.color || undefined,
      });
    if (presentation.hasAttributes === "model")
      Object.assign(attributes, {
        version: values.version || undefined,
        photoUrl: values.photoUrl || undefined,
        // PLAN-007 Fase 4d: viaja solo cuando el operador subió un archivo
        // nuevo en esta sesión; el backend le da prioridad sobre `photoUrl`.
        ...(values.photoFileId ? { photoFileId: values.photoFileId } : {}),
      });
    if (presentation.hasAttributes === "color")
      Object.assign(attributes, { hex: values.hex || undefined });
    if (presentation.hasAttributes === "maintenance")
      Object.assign(attributes, { referenceKm: data.referenceKm });
    if (presentation.hasAttributes === "service")
      Object.assign(attributes, { durationMin: data.durationMin });
    if (presentation.hasAttributes === "workshop") {
      // P8-3 (SPEC-207 §3, resolución P8A-01): el horario de atención se
      // edita solo en Agendamiento — este catálogo ya no manda `hours`.
      Object.assign(attributes, {
        cityId: values.cityId || undefined,
        address: values.address || null,
        phone: values.phone || null,
        bays: data.bays,
        noticeEmails: values.noticeEmails
          ? values.noticeEmails
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : undefined,
      });
    }
    if (presentation.hasAttributes === "merchant")
      Object.assign(attributes, {
        type: values.type || undefined,
        cityId: values.cityId || undefined,
        address: values.address || null,
        contact: values.contact || null,
        phone: values.phone || null,
        logoUrl: values.logoUrl || null,
        // PLAN-007 Fase 4d: viaja solo cuando el operador subió un archivo
        // nuevo en esta sesión; el backend le da prioridad sobre `logoUrl`
        // (MerchantInputAttributes.logoFileId).
        ...(values.logoFileId ? { logoFileId: values.logoFileId } : {}),
      });
    const common = {
      name,
      description,
      ...(data.sortOrder === undefined ? {} : { sortOrder: data.sortOrder }),
      ...(Object.keys(attributes).length ? { attributes } : {}),
    };
    onSubmit(
      entry
        ? ({ ...common, confirmed: false } as CatalogEntryUpdate)
        : ({
            ...common,
            code: values.code,
            active: values.active,
          } as CatalogEntryInput),
    );
  };
  return (
    <FormLayout
      eyebrow={entry ? "Editar entrada" : "Nueva entrada"}
      title={nameEs || "Entrada sin nombre"}
      bilingual={bilingualEntry}
      language={language}
      onLanguageChange={setLanguage}
      {...(modal ? { modal } : {})}
      onSubmit={handleSubmit(submit)}
      actions={
        <>
          <PortalButton
            type="button"
            tone="secondary"
            size="small"
            onClick={onCancel}
          >
            Cancelar
          </PortalButton>
          <PortalButton type="submit" size="small" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar"}
          </PortalButton>
        </>
      }
    >
      {!entry ? (
        <Field label="Código" required error={errors.code?.message}>
          <input {...register("code")} style={inputStyle} />
        </Field>
      ) : (
        <Field label="Código">
          <input
            value={entry.code}
            readOnly
            aria-label="Código no editable"
            style={{ ...inputStyle, opacity: 0.65 }}
          />
          <small style={{ color: tokens.text.tertiary }}>
            El código no se puede editar.
          </small>
        </Field>
      )}
      <Field
        label={`Nombre (${language.toUpperCase()})`}
        required
        error={
          language === "es" ? errors.nameEs?.message : errors.nameEn?.message
        }
      >
        {/* `key={language}` remonta el campo al cambiar de idioma: sin él
          react-hook-form no reescribe el valor del nodo ya registrado y el
          ciclo ES → EN → ES dejaba el inglés bajo el campo español
          (QA-MTZ-002 N-8, mismo defecto que #1). */}
        <input
          key={language}
          {...register(language === "es" ? "nameEs" : "nameEn")}
          placeholder={language === "en" ? untranslatedPlaceholder : undefined}
          style={inputStyle}
        />
      </Field>
      <Field label={`Descripción (${language.toUpperCase()})`}>
        <textarea
          key={language}
          {...register(language === "es" ? "descriptionEs" : "descriptionEn")}
          placeholder={language === "en" ? untranslatedPlaceholder : undefined}
          style={inputStyle}
          rows={3}
        />
      </Field>
      <Field label="Orden" error={errors.sortOrder?.message}>
        <input type="number" {...register("sortOrder")} style={inputStyle} />
      </Field>
      {presentation.hasAttributes === "action" ? (
        <>
          <Field label="Color">
            <input
              {...register("color")}
              placeholder="#E30613"
              style={inputStyle}
            />
          </Field>
          {catalog === "referral-actions" ? (
            <div>
              <Checkbox
                name="isFinal"
                checked={Boolean(isFinal)}
                onChange={(next) =>
                  setValue("isFinal", next, { shouldDirty: true })
                }
                label="Marcar como acción final"
              />
            </div>
          ) : null}
        </>
      ) : null}
      {presentation.hasAttributes === "model" ? (
        <>
          <Field label="Versión">
            <input {...register("version")} style={inputStyle} />
          </Field>
          <Field label="Fotografía">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ImagePreviewBox imageUrl={photoUrl} />
              <FileUploadChip
                entityType="car_model"
                label="Reemplazar"
                ariaLabel="Subir fotografía del modelo"
                onUploaded={(image) => {
                  // PLAN-007 Fase 4d: `imageUrl` solo para la
                  // previsualización inmediata (vigente 15 minutos); el
                  // `fileId` es lo que de verdad viaja al servidor.
                  setValue("photoUrl", image.imageUrl, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue("photoFileId", image.fileId, {
                    shouldDirty: true,
                  });
                }}
              />
            </div>
          </Field>
        </>
      ) : null}
      {presentation.hasAttributes === "color" ? (
        <Field label="Hexadecimal" error={errors.hex?.message}>
          <input
            {...register("hex")}
            placeholder="#E30613"
            style={inputStyle}
          />
        </Field>
      ) : null}
      {presentation.hasAttributes === "maintenance" ? (
        <Field
          label="Kilometraje de referencia"
          error={errors.referenceKm?.message}
        >
          <input
            type="number"
            {...register("referenceKm")}
            style={inputStyle}
          />
        </Field>
      ) : null}
      {presentation.hasAttributes === "service" ? (
        <Field label="Duración (minutos)" error={errors.durationMin?.message}>
          <input
            type="number"
            {...register("durationMin")}
            style={inputStyle}
          />
        </Field>
      ) : null}
      {presentation.hasAttributes === "workshop" ? (
        <>
          <CityField
            cities={cities.data}
            register={register}
            error={errors.cityId?.message}
          />
          <Field label="Dirección">
            <input {...register("address")} style={inputStyle} />
          </Field>
          <Field label="Teléfono">
            <input {...register("phone")} style={inputStyle} />
          </Field>
          <Field label="Bahías" error={errors.bays?.message}>
            <input type="number" {...register("bays")} style={inputStyle} />
          </Field>
          <Field label="Correos de aviso" error={errors.noticeEmails?.message}>
            <input {...register("noticeEmails")} style={inputStyle} />
          </Field>
        </>
      ) : null}
      {presentation.hasAttributes === "merchant" ? (
        <>
          <Field label="Tipo de aliado" error={errors.type?.message}>
            <select {...register("type")} style={inputStyle}>
              <option value="">Selecciona un tipo</option>
              {MERCHANT_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <CityField
            cities={cities.data}
            register={register}
            error={errors.cityId?.message}
          />
          <Field label="Dirección">
            <input {...register("address")} style={inputStyle} />
          </Field>
          <Field label="Contacto">
            <input {...register("contact")} style={inputStyle} />
          </Field>
          <Field label="Teléfono">
            <input {...register("phone")} style={inputStyle} />
          </Field>
          <Field label="Logotipo">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ImagePreviewBox imageUrl={logoUrl} />
              <FileUploadChip
                entityType="merchant"
                label="Reemplazar"
                ariaLabel="Subir logotipo del comercio"
                onUploaded={(image) => {
                  // PLAN-007 Fase 4d: `imageUrl` solo para la
                  // previsualización inmediata (vigente 15 minutos); el
                  // `fileId` es lo que de verdad viaja al servidor.
                  setValue("logoUrl", image.imageUrl, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue("logoFileId", image.fileId, {
                    shouldDirty: true,
                  });
                }}
              />
            </div>
          </Field>
          <div>
            <strong>Usuarios validadores asociados</strong>
            {(() => {
              const validators = Array.isArray(
                (entry?.attributes as Record<string, unknown> | undefined)
                  ?.validators,
              )
                ? ((entry?.attributes as Record<string, unknown>)
                    .validators as Array<{
                    id: string;
                    name: string;
                    active: boolean;
                  }>)
                : [];
              if (!validators.length)
                return (
                  <p style={{ color: tokens.text.tertiary }}>
                    Aún no hay usuarios validadores. Se crean en{" "}
                    <b style={{ color: tokens.text.secondary }}>
                      Seguridad → Usuarios
                    </b>{" "}
                    con el perfil Comercio.
                  </p>
                );
              return validators.map((validator) => (
                <div key={validator.id}>
                  {validator.name} · {validator.active ? "Activo" : "Inactivo"}
                </div>
              ));
            })()}
          </div>
        </>
      ) : null}
      {!entry ? (
        <div>
          <Checkbox
            name="active"
            checked={Boolean(active)}
            onChange={(next) => setValue("active", next, { shouldDirty: true })}
            label="Activo"
          />
        </div>
      ) : null}
    </FormLayout>
  );
}
